import { action, makeObservable, observable, runInAction } from "mobx";
import { randmarApiWithTags, type InsertProductInput } from "randmar-api-client";
import { store } from "../../new-store/store";
import { shortMonthNames } from "../../components/util/DateTimeUtil";
import { OrderDetail } from "../document/Document";
import { Product } from "./Product";

class ProductStore {
  product?: Product = undefined;

  constructor() {
    makeObservable(this, {
      product: observable,
      getProduct: action.bound,
      setProductProperty: action.bound,
      discontinueProduct: action.bound,
      uploadProductPicture: action.bound,
      addNewProduct: action.bound,
      addToRequisition: action.bound,
      addInstantRebate: action.bound
    });
    console.warn(
      "Deprecation Warning: The legacy 'ProductStore' is in use. Please migrate to RTK Query as outlined in CONTRIBUTING.md."
    );
  }

  async getProduct(applicationId: string, randmarSKU: string) {
    try {
      if (this.product?.randmarSKU !== randmarSKU) {
        this.product = undefined;
      }

      const productWithSpecification = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdProductAndProductNumber.initiate({
          routeManufacturerId: applicationId,
          productNumber: randmarSKU,
          withSpecification: true,
        })
      ).unwrap();
      const mappedProductWithSpecification = Product.convertFrom(productWithSpecification);
      runInAction(() => { this.product = mappedProductWithSpecification; });

    } catch (e) {
      console.error(e);
    }
  }

  getMonthlySalesData(orders: OrderDetail[], monthsBack: number) {
    const numberToDate = (dateInt: number) => {
      const dateStr = dateInt.toString();
      const year = parseInt(dateStr.slice(0, 4), 10);
      const month = parseInt(dateStr.slice(4, 6), 10) - 1;
      return new Date(year, month);
    };

    const currentDate = new Date();
    const earliestOrderDate = orders.reduce((earliest, item) => {
      if (item.orderDate) {
        const orderDate = numberToDate(item.orderDate);
        return orderDate < earliest ? orderDate : earliest;
      }
      return earliest;
    }, new Date());

    if (monthsBack === Infinity) {
      const totalMonths = (currentDate.getFullYear() - earliestOrderDate.getFullYear()) * 12
        + currentDate.getMonth() - earliestOrderDate.getMonth() + 1;
      monthsBack = totalMonths;
    }

    const lastMonths: { [key: string]: number } = {};

    const getMonthYearKey = (date: Date) => {
      const month = shortMonthNames[date.getMonth()];
      const year = date.getFullYear().toString().slice(-2);
      return `${month} '${year}`;
    };

    for (let i = 0; i < monthsBack; i++) {
      const date = new Date(currentDate);
      date.setMonth(currentDate.getMonth() - i);
      lastMonths[getMonthYearKey(date)] = 0;
    }

    orders.forEach(item => {
      if (item.orderDate) {
        const orderDate = numberToDate(item.orderDate);
        const key = getMonthYearKey(orderDate);
        if (Object.prototype.hasOwnProperty.call(lastMonths, key)) {
          lastMonths[key] += item.quantity ?? 0;
        }
      }
    });

    const currentMonthYearKey = getMonthYearKey(currentDate);
    const daysInCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    const daysElapsed = currentDate.getDate();

    if (lastMonths[currentMonthYearKey] > 0) {
      lastMonths[currentMonthYearKey] = Math.round(
        (lastMonths[currentMonthYearKey] / daysElapsed) * daysInCurrentMonth
      );
    }

    return Object.keys(lastMonths)
      .sort((a, b) => new Date(`1 ${a}`).getTime() - new Date(`1 ${b}`).getTime())
      .map(month => ({
        "Month": monthsBack <= 12 ? month.slice(0, 3) : month,
        "Units Sold": lastMonths[month]
      }));
  }

  async setProductProperty(
    applicationId: string,
    randmarSKU: string,
    propName: string,
    newValue: string
  ): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdProductAndProductNumberFieldName.initiate({
          routeManufacturerId: applicationId,
          productNumber: randmarSKU,
          fieldName: propName,
          newValue,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async setProductBodyProperty(applicationId: string, randmarSKU: string, propName: string, newValue: string): Promise<boolean> {
    try {
      if (propName !== "BodyHTML") {
        console.error(`Unsupported body field: ${propName}`);
        return false;
      }
      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdProductAndProductNumberBodyHtml.initiate({
          routeManufacturerId: applicationId,
          productNumber: randmarSKU, body: new Blob([JSON.stringify(newValue)], { type: 'application/json' }),
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async discontinueProduct(applicationId: string, randmarSKU: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdProductAndProductNumberDiscontinue.initiate({
          routeManufacturerId: applicationId,
          productNumber: randmarSKU,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async uploadProductPicture(applicationId: string, randmarSKU: string, selectedFile: File): Promise<boolean> {
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdProductAndRandmarSkuImage.initiate({
          routeManufacturerId: applicationId,
          randmarSku: randmarSKU,
          body: formData as unknown as never,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async uploadProduct3DModel(applicationId: string, randmarSKU: string, selectedFile: File): Promise<boolean> {
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdProductAndRandmarSku3DModel.initiate({
          routeManufacturerId: applicationId,
          randmarSku: randmarSKU,
          body: formData as unknown as never,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async addNewProduct(applicationId: string, _upc: string, product: InsertProductInput): Promise<Product | undefined> {
    try {
      const randmarProduct = await store.dispatch(
        randmarApiWithTags.endpoints.putV4ManufacturerByRouteManufacturerIdProduct.initiate({
          routeManufacturerId: applicationId,
          insertProductInput: product,
        })
      ).unwrap();

      if (randmarProduct === undefined) return undefined;

      return Product.convertFrom(randmarProduct);

    } catch {
      return undefined;
    }
  }

  async addToRequisition(
    applicationId: string,
    randmarSKU: string,
    location: string,
    quantity: string
  ): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.putV4ManufacturerByRouteManufacturerIdProductAndRandmarSkuRequisition.initiate({
          routeManufacturerId: applicationId,
          randmarSku: randmarSKU,
          location,
          quantity: Number(quantity),
        })
      ).unwrap();
      return true;
    } catch {
      return false;
    }
  }

  async addInstantRebate(
    applicationId: string,
    randmarSKU: string,
    rebate: number,
    startDate: string,
    endDate: string,
    promotionId: string
  ): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.putV4ManufacturerByRouteManufacturerIdProductAndRandmarSkuInstantRebate.initiate({
          routeManufacturerId: applicationId,
          randmarSku: randmarSKU,
          instantRebateInput: {
            Rebate: rebate,
            StartDate: startDate,
            EndDate: endDate,
            PromotionId: promotionId,
          },
        })
      ).unwrap();
      return true;
    } catch {
      return false;
    }
  }
}

export default ProductStore;
